/**
 * <load-more-button data-section-id="..."> — replaces the standard pagination
 * UI with a Load More button that fetches the next page via fetch() and appends
 * its product cards to the current grid in-place.
 *
 * Falls back to standard pagination if anything goes wrong, and re-initializes
 * itself when the facets/filters system re-renders the section.
 */
class LoadMoreButton extends HTMLElement {
  constructor() {
    super();
    this.handleClick = this.handleClick.bind(this);
  }

  connectedCallback() {
    if (this.dataset.initialized === 'true') return;
    this.dataset.initialized = 'true';

    this.sectionId = this.getAttribute('data-section-id') || '';
    this.scope = this.closest('.collection') || document;

    this.pagination = this.scope.querySelector('.pagination-wrapper');
    if (!this.pagination) {
      this.style.display = 'none';
      return;
    }

    const nextLink = this.findNextLink(this.pagination);
    if (!nextLink) {
      this.pagination.style.display = 'none';
      this.style.display = 'none';
      return;
    }

    this.nextUrl = nextLink.getAttribute('href');
    this.pagination.style.display = 'none';

    this.renderButton();
  }

  findNextLink(paginationWrapper) {
    // Theme's pagination snippet uses aria-label='Next' (i18n) on the next-page link.
    // Fallback: last anchor with a href containing 'page=' that's not 'page=1'.
    let link = paginationWrapper.querySelector('a.pagination__item--prev, a[aria-label*="ext"], a[aria-label*="iguiente"], a[aria-label*="rochaine"]');
    if (link) return link;
    const all = Array.from(paginationWrapper.querySelectorAll('a[href*="page="]'));
    return all[all.length - 1] || null;
  }

  renderButton() {
    if (this.button) return;
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'load-more-button button button--secondary';
    btn.textContent = 'Load More';
    btn.addEventListener('click', this.handleClick);
    this.appendChild(btn);
    this.button = btn;
  }

  setBusy(busy) {
    if (!this.button) return;
    this.button.disabled = busy;
    this.button.classList.toggle('is-loading', busy);
    this.button.textContent = busy ? 'Loading…' : 'Load More';
  }

  async handleClick() {
    if (this.loading || !this.nextUrl) return;
    this.loading = true;
    this.setBusy(true);
    try {
      const res = await fetch(this.nextUrl, { headers: { Accept: 'text/html' }, credentials: 'same-origin' });
      if (!res.ok) throw new Error('HTTP ' + res.status);
      const html = await res.text();
      const doc = new DOMParser().parseFromString(html, 'text/html');

      const currentGrid = document.querySelector(`#product-grid[data-id="${this.sectionId}"]`)
                       || document.getElementById('product-grid');
      const nextGrid = doc.querySelector(`#product-grid[data-id="${this.sectionId}"]`)
                    || doc.getElementById('product-grid');

      if (!currentGrid || !nextGrid) throw new Error('product grid not found in response');

      const newCards = nextGrid.querySelectorAll(':scope > li');
      const fragment = document.createDocumentFragment();
      newCards.forEach((node) => fragment.appendChild(node.cloneNode(true)));
      currentGrid.appendChild(fragment);

      const newPagination = doc.querySelector('.pagination-wrapper');
      const newNext = newPagination ? this.findNextLink(newPagination) : null;
      if (newNext) {
        this.nextUrl = newNext.getAttribute('href');
        this.setBusy(false);
      } else {
        this.button.remove();
        this.button = null;
      }

      // Update browser URL silently so users can deep-link to "page N" loaded state.
      try {
        const url = new URL(window.location.href);
        const fetched = new URL(this.nextUrl || (newNext ? newNext.href : ''), window.location.origin);
        const current = fetched.searchParams.get('page');
        if (current) {
          url.searchParams.set('page', String(parseInt(current, 10) - 1));
        }
        history.replaceState({}, '', url.toString());
      } catch (_) { /* non-fatal */ }

      document.dispatchEvent(new CustomEvent('load-more:appended', {
        detail: { sectionId: this.sectionId, count: newCards.length },
      }));
    } catch (err) {
      console.error('[load-more] failed:', err);
      // Reveal native pagination as graceful fallback.
      if (this.pagination) this.pagination.style.display = '';
      this.style.display = 'none';
    } finally {
      this.loading = false;
    }
  }
}

if (!customElements.get('load-more-button')) {
  customElements.define('load-more-button', LoadMoreButton);
}
